import { Box, Button, Fade, Paper, TextField } from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import {
	BaseFormPackageProcessorState,
	ConfigurableFormPackageProcessor,
	FormPackageProcessorInstance,
} from '../../../../../../../../api/DTOtemp'
import { FormPackageProcessorInstancesClient } from '../../../../../../../../api/clients/identity/FormPackageProcessorInstancesClient'
import { FormPackageProcessorsClient } from '../../../../../../../../api/clients/identity/FormPackageProcessorsClient'
import { FullscreenSpinner } from '../../../../../../../../components/feedback/circular'
import {
	FormBuilderViewer,
	StandaloneCellManager,
} from '../../../../../../../../modules/FormBuilderInterop/Runtimes/Standalone'
import { toastService } from '../../../../../../../../services/notifications/ToastService'
import { FixedHeader } from '../../../../../../../../utils/HOC/FixedHeaders'
import AdministrationPageContainer from '../../../../../AdministrationPageContainer'

type FormPackageProcessorInstancePageProps = {
	packageId: number
}

export const FormPackageProcessorInstancePage = observer(
	(props: FormPackageProcessorInstancePageProps) => {
		const params = useParams<{ processorInstanceId: string }>()

		const instanceId = parseInt(params.processorInstanceId)
		if (isNaN(instanceId))
			throw new Error(
				`invalid processor instance id: '${params.processorInstanceId}'`,
			)

		const localStore = useLocalObservable(() => ({
			instance: undefined as FormPackageProcessorInstance | undefined,
			processor: undefined as ConfigurableFormPackageProcessor | undefined,
			instanceState: {} as BaseFormPackageProcessorState,
			name: '',
			isSaving: false,
		}))

		const processorInstancesClient = new FormPackageProcessorInstancesClient(
			props.packageId,
		)
		const processorsClient = new FormPackageProcessorsClient()

		useEffect(() => {
			if (localStore.instance === undefined) fetchInstance()
		}, [localStore.instance, instanceId])

		const fetchInstance = async () => {
			const { data: instance } =
				await processorInstancesClient.getFormPackageProcessorInstance(
					instanceId,
				)
			const { data: processor } =
				await processorsClient.getFormPackageProcessor(instance.processorId)

			runInAction(() => {
				localStore.instance = instance
				localStore.processor = processor
				localStore.instanceState = instance.state
				localStore.name = instance.name
			})
		}

		const cellManager = useMemo(() => {
			if (
				localStore.instance === undefined ||
				localStore.processor === undefined
			)
				return undefined

			return new StandaloneCellManager(
				localStore.processor.formBuilderSchema,
				localStore.instanceState,
				!localStore.instance.isActive,
			)
		}, [localStore.instance, localStore.instanceState, localStore.processor])

		// b/c we're updating the package version we have to create a new instance
		const handleUpdateInstance = async () => {
			if (
				localStore.processor === undefined ||
				localStore.instance === undefined
			)
				return

			try {
				runInAction(() => {
					localStore.isSaving = true
				})

				const { data: updatedInstance } =
					await processorInstancesClient.updateProcessorInstance(
						localStore.instance.id,
						{
							processorId: localStore.processor.processorId,
							name: localStore.name,
							state: localStore.instanceState,
							description: localStore.instanceState.description ?? '',
						},
					)

				runInAction(() => {
					localStore.instance = updatedInstance
					localStore.instanceState = updatedInstance.state
				})

				toastService.displayToast({
					message: 'Processor updated',
					area: 'global',
					severity: 'success',
				})
			} catch (error) {
				console.error(error)

				toastService.displayToast({
					message: 'Failed to update processor',
					area: 'global',
					severity: 'error',
				})
			} finally {
				runInAction(() => {
					localStore.isSaving = false
				})
			}
		}

		const saveIsDisabled = useMemo(() => {
			if (
				localStore.instance === undefined ||
				localStore.instanceState === undefined ||
				localStore.instance.isActive === false
			)
				return true

			if (localStore.isSaving) return true

			// we have to do it this way bc the form builder adds "$props" keys to the state
			// so we have to loop thru the values and compare. I don't love comparing with
			// stringify but that's the only option here :/
			for (const stateKey of Object.keys(localStore.instance.state))
				if (
					localStore.instanceState[stateKey] !== undefined &&
					JSON.stringify(localStore.instanceState[stateKey]) !==
						JSON.stringify(localStore.instance.state[stateKey])
				)
					return false

			if (localStore.name !== localStore.instance.name) return false

			return true
		}, [
			Object.values(localStore.instanceState),
			localStore.instanceState,
			localStore.name,
			localStore.instance,
			localStore.isSaving,
		])

		if (localStore.instance === undefined) return <FullscreenSpinner />

		return (
			<AdministrationPageContainer title={localStore.instance.name}>
				<Fade in>
					<Box
						component={Paper}
						width="100%"
						marginX={-2}
						marginY={2}
						paddingX={2}
					>
						<Box display="flex" flexDirection="column" gap={1}>
							<FixedHeader label="Name">
								<TextField
									fullWidth
									disabled={!localStore.instance.isActive}
									size="small"
									value={localStore.name}
									onChange={action(
										(evt) => (localStore.name = evt.target.value),
									)}
								/>
							</FixedHeader>
							{cellManager !== undefined && (
								<FormBuilderViewer cellManager={cellManager} />
							)}
						</Box>
						<Box padding={2} display="flex" justifyContent="flex-end">
							<Button
								color="primary"
								variant="contained"
								onClick={handleUpdateInstance}
								disabled={saveIsDisabled}
							>
								Save Changes
							</Button>
						</Box>
					</Box>
				</Fade>
			</AdministrationPageContainer>
		)
	},
)
